"use client";

/**
 * SwitchModeDisplay — tool display for switch_mode("alert" | "research").
 *
 * Matches the "Refining approach" shape exactly for uniform thickness across
 * every in-stream tool card: rounded-md, px-3 py-2, inline w-3.5 h-3.5 icon
 * (no icon-square), text-[13px] font-medium.
 *
 * - Alert mode → ICY accent color.
 * - Research mode → neutral slate.
 * - Optional right-side BUILDING chip when entering alert mode.
 */

import { motion } from "framer-motion";
import { Waypoints, FlaskConical, Loader2 } from "lucide-react";

interface SwitchModeDisplayProps {
  status: "loading" | "success";
  result?: {
    status?: string;
    view_mode?: string;
    alert_start_cell_index?: number | null;
    noop?: boolean;
    message?: string;
  };
  args?: {
    mode?: string;
  };
}

export function SwitchModeDisplay({
  status,
  result,
  args,
}: SwitchModeDisplayProps) {
  const mode =
    (result?.view_mode || args?.mode || "").toString().toLowerCase() ||
    "research";
  const isSignal = mode === "signal";
  // Both alert AND signal are "build" modes — same canvas, same chips.
  const isAlert = mode === "alert" || isSignal;
  const label = isSignal ? "Signal mode" : isAlert ? "Alert mode" : "Researching";
  const noop = result?.noop === true;

  // Palettes: icy-blue for alert, emerald for signal, neutral slate for research.
  // Matched height + padding to GenericError / ErrorLoggingDisplay.
  const borderClass = isSignal
    ? "border-emerald-300/25 bg-emerald-300/[0.03]"
    : isAlert
      ? "border-sky-300/25 bg-sky-300/[0.03]"
      : "border-slate-400/20 bg-slate-900/40";
  const iconClass = isSignal ? "text-emerald-100" : isAlert ? "text-sky-100" : "text-slate-200";
  const textClass = isSignal ? "text-emerald-50" : isAlert ? "text-sky-50" : "text-slate-50";

  const Icon = status === "loading" ? Loader2 : isAlert ? Waypoints : FlaskConical;
  const spin = status === "loading";

  return (
    <motion.div
      initial={{ opacity: 0, y: 4, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      className="w-full"
    >
      <div className={`rounded-md border overflow-hidden ${borderClass}`}>
        <div className="px-3 py-2 flex items-center gap-2.5 min-w-0">
          <Icon
            className={`w-3.5 h-3.5 shrink-0 ${iconClass} ${spin ? "animate-spin" : ""}`}
            strokeWidth={2}
          />
          <span className={`text-[13px] font-medium truncate ${textClass}`}>
            {status === "loading"
              ? isSignal
                ? "Opening signal mode"
                : isAlert
                  ? "Opening alert mode"
                  : "Returning to research"
              : label}
          </span>

          <span className="flex-1" />

          {status === "success" && isAlert && !noop && (
            <span className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider tabular-nums ${
              isSignal
                ? "border-emerald-300/35 bg-emerald-300/[0.08] text-emerald-50"
                : "border-sky-300/35 bg-sky-300/[0.08] text-sky-50"
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${isSignal ? "bg-emerald-200" : "bg-sky-200"}`} />
              BUILDING
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}
